import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  IS_UPCOMING_SESSIONS_ENABLED,
  useIsNewSessionListEnabled,
  useIsOfflineRecordingEnabled,
  useIsSessionRoutineRunsEnabled,
} from '@/features/flags';
import { onSessionCreatedExpectRoutineRuns } from '@/features/workflows/lib/session-routine-runs-expectation';
import { releaseRecordingStateAfterSweep } from '@/hooks/desktop/releaseRecordingStateAfterSweep';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { sessionCreated } from '@/lib/analytics/scribe/session';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import {
  cancelSessionCreateAttempt,
  classifySessionCreateError,
  failOwnedSessionCreateAttempt,
  type SessionCreateAttemptOwner,
} from '@/lib/telemetry/sessionCreateTracking';
import { SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { trackSessionCreated } from '@/models/analytics/features/trackSessionLifecycleAnalytics';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { postCreateSessionV2 } from '@/pages/api/scribeAPIV2';
import { sweepCaptureOnSessionEnd } from '@/tauri/online-chunk-upload/session-end-sweep';
import { isTauri } from '@/tauri/utils';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { mapNewBrain } from '@/utils/brainUtils';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { hasServerResponse, isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import { getSessionCreationPlatform } from '@/utils/sessionCreationPlatform';
import { datadogRum } from '@datadog/browser-rum';
import { WORKFLOW_KEYS } from '@heidi/agents/contracts';
import { toast } from '@heidi/design-system/toast';
import { emitDomainEvent } from '@heidi/runtime/events';
import { sanitizeError } from '@heidi/runtime/logger';
import { appendCheckpoint, TrackingKey } from '@heidi/runtime/performance';
import { BaseStatus, SessionGroupStatus, SessionStatus } from '@heidi/schema/ml-scribe';
import {
  BackupUploadingStatus,
  buildSessionPath,
  CREATE_NEW_SESSION_MUTATION_KEY,
  DEFAULT_TEMPLATE_AUTO_MODE,
  GET_SESSION_BY_ID,
  isLegacySessionDetailRoute,
  NEW_SESSION_LIST_DETAIL_ROUTE,
  NEW_SESSION_LIST_PATH_PREFIX,
  type QuerySessionResponse,
  sessionCreatedEvent,
  type SessionsResponse,
  type SimpleSessionResponse,
} from '@heidi/session/contracts';
import {
  openedSessionTabAtom,
  openedSessionTabEnum,
  pendingStartNowSessionIdAtom,
} from '@heidi/session/data';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { isBefore } from 'date-fns';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { type ParsedUrlQueryInput } from 'querystring';
import { GET_SESSIONS } from '../query/useQuerySessions';
import { GET_INFINITE_SESSIONS } from '../query/useQuerySessionsV2';
import { GET_INFINITE_SESSIONS_WITH_PAGINATION } from '../query/useQuerySessionsWithPagination';
import { STOP_RECORDING_MUTATION_KEY } from './recordingMutationKeys';

type Props = {
  navigate: boolean;
  queryParams?: ParsedUrlQueryInput;
  entryPoint?: SessionCreatedEntryPoint;
  /**
   * Patient-surface view_id to stitch the open -> session_created funnel.
   * Set only when the session is started from the patient surface.
   */
  viewId?: string;
  /**
   * Set only by a caller that catches this rejection and re-creates the session
   * through offline capture. It suppresses the network-error toast for a create
   * that caller then rescues. Every other caller leaves it unset and keeps the
   * toast, because for them a rejected create really is a failure the clinician
   * has to be told about.
   */
  handlesOfflineCaptureFallback?: boolean;
  /**
   * Park Start Now before navigating so the session page auto-starts
   * transcription (same mechanism as the session-list Start Now action).
   */
  autoStartTranscription?: boolean;
};

/** Set only by the caller that opened the `session_create` attempt. */
export type CreateNewSessionVariables = { attempt?: SessionCreateAttemptOwner } | void;

export function useMutateCreateNewSession(
  {
    navigate,
    queryParams,
    entryPoint,
    viewId,
    handlesOfflineCaptureFallback = false,
    autoStartTranscription = false,
  }: Props = { navigate: true }
) {
  const queryClient = useQueryClient();
  const jotaiStore = useStore();
  const router = useRouter();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const { isFreeUser } = useIsFreeUser();
  const openedSessionTab = useAtomValue(openedSessionTabAtom);
  const setOpenedSessionTab = useSetAtom(openedSessionTabAtom);
  const { data: user } = useQueryUser();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isSessionRoutineRunsEnabled = useIsSessionRoutineRunsEnabled();
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  // Opt-in rather than inferred from platform + flag: `onError` is hook-level
  // and runs for every consumer, so inferring it would silence the toast for
  // callers that never implement the fallback and have no other signal.
  const canAttemptOfflineSessionCreate =
    handlesOfflineCaptureFallback && isOfflineRecordingEnabled && isTauri();

  const buildSessionRedirectUrl = (sessionId: string) => {
    const searchParams = new URLSearchParams();
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
          for (const item of value) {
            searchParams.append(key, String(item));
          }
          continue;
        }
        searchParams.set(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    // A user already sitting on the legacy page stays on it, so the flag alone
    // does not decide the route here.
    const basePath = buildSessionPath(
      sessionId,
      isNewSessionListEnabled && !isLegacySessionDetailRoute(router.pathname)
    );
    return appendAttributionParamsToRedirectUrl(
      queryString ? `${basePath}?${queryString}` : basePath
    );
  };

  return useMutation({
    mutationKey: [CREATE_NEW_SESSION_MUTATION_KEY],
    mutationFn: async (variables: CreateNewSessionVariables) => {
      const attempt = variables ? variables.attempt : undefined;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userDefaultTemplateId = user?.session_settings?.default_template_id;
      const is_auto_mode = userDefaultTemplateId === DEFAULT_TEMPLATE_AUTO_MODE;
      const creation_platform = getSessionCreationPlatform({ isHardware: false });
      // 60s deadline prevents a hung POST from pinning the mutation in `pending`
      // forever, which would trap `useNewSessionMutationPending()` users on the
      // session-page skeleton. 60s chosen because RUM shows the legitimate-success
      // P99 for this endpoint sits around 34s; a 30s deadline would error the
      // slow tail.
      let response: Awaited<ReturnType<typeof postCreateSessionV2>>;
      try {
        appendCheckpoint({
          key: TrackingKey.SCRIBE_NEW_SESSION,
          checkpointName: 'request_sent',
        });
        response = await postCreateSessionV2({
          timezone,
          is_auto_mode,
          creation_platform,
          signal: AbortSignal.timeout(60_000),
        });
        appendCheckpoint({
          key: TrackingKey.SCRIBE_NEW_SESSION,
          checkpointName: 'response_received',
        });
        // The HTTP layer resolves empty when a 401 or a suspended account
        // takes the user out of the app, so no session was created.
        if (!response && attempt) cancelSessionCreateAttempt('interrupted');
      } catch (error) {
        // The 60s deadline rejects as axios `CanceledError`; the DOMException
        // check covers a non-axios transport.
        const isAbort =
          (error instanceof DOMException && error.name === 'TimeoutError') ||
          (error as { code?: unknown } | null)?.code === 'ERR_CANCELED';
        const context = {
          step: 'post_create_session',
          timezone,
          isAutoMode: is_auto_mode,
          isAbort,
        };
        failOwnedSessionCreateAttempt(attempt, error);
        // A classification, never the raw error: an AxiosError can echo
        // account details from the request or response.
        logger.error('[useMutateCreateNewSession] mutationFn failed', {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
          ...context,
        });
        datadogRum.addError(classifySessionCreateError(error), {
          source: 'use-mutate-create-new-session.mutationFn',
          ...context,
        });
        throw error;
      }

      // FR-038, and the placement is the requirement, not an implementation
      // detail: capture may only be stopped once the session it belongs to has
      // ACTUALLY ENDED — not once we intend to end it.
      //
      // So this sits AFTER the create has succeeded, on the last awaited line
      // before `onSuccess` pushes the redirect. A failed create does not
      // navigate: the clinician stays on the same patient's page, still
      // consulting, and nothing else rescues them either — `isNetworkCreateError`
      // is false for a server-answered error, so the offline fallback does not
      // fire. Sweeping before the create therefore ends capture on a
      // consultation that is still going.
      //
      // Gated on `navigate`, and the gate is NARROWER than the requirement.
      // FR-038's shape is "mints a session AND NAVIGATES AWAY", but
      // `navigate: false` does not mean the clinician stays put — it means the
      // CALLER navigates. Six of the seven live consumers push to the session
      // they just minted on their next line: the patient-profile drawer
      // (`useCreatePatientSession.ts`), the template builder
      // (`useTemplateForm.tsx`), the GDT launch completion
      // (`useGdtLaunchComplete.ts`), the GDT test-mode panel
      // (`gdt-test-mode-panel.tsx`), the EHR-patient link
      // (`useLinkEhrPatientToSession.ts`) and the Epic launch completion
      // (`useEpicLaunchComplete.ts`). This gate excludes all six, which is a
      // PRE-EXISTING gap FR-038 does not close and a follow-up owns; they are
      // listed with their pushing lines under "Not closed by FR-038" in
      // `specs/012-app-8723-self-healing-stop/spec.md`.
      //
      // The seventh, `WorkSessionsSection.tsx`, is the only true "stays put"
      // case: it opens the minted session as an in-page artifact panel and
      // imports no router.
      //
      // It does NOT cover the other two creators `useNewSession` routes to; each
      // sweeps on its own success path
      // (`useMutateCreateNewSessionForOtherUser`, and `useMutateCreateOfflineSession`
      // which `routeOffline()` reaches FIRST when connectivity has degraded
      // mid-recording).
      //
      // `anyActiveUpload` because the user's involvement with whatever is still
      // recording is ending here, whichever session native names it under.
      // Awaited so capture is terminated before the redirect, and safe to
      // ignore: the helper never throws and never blocks the create on failure.
      if (navigate) {
        // The user's own stop holds `RECORDING_STOP_COORDINATOR`
        // (`recording_lifecycle/terminal.rs`) for its whole drain, and this sweep
        // sends no `recordingId`, so its stop resolves to that same epoch and
        // could only wait out `SESSION_END_SWEEP_TIMEOUT_MS` on the lock.
        //
        // `isMutating` on the stop key, NOT
        // `hasOpenRustChunkUploadPauseFinalization`: that registry is opened only
        // for a PAUSED stop and is cleared by a non-paused one
        // (`useMutateStopRecording.ts`), so it is false during an End-transcribing
        // stop and would make this a silent no-op.
        const sweepResult = await sweepCaptureOnSessionEnd({
          target: { kind: 'anyActiveUpload' },
          callSite: 'createNewSession',
          stopInFlight:
            queryClient.isMutating({ mutationKey: [STOP_RECORDING_MUTATION_KEY] }) > 0,
        });

        // Stopping native capture is only half the job, and the half that was
        // missing is the one the NEXT recording depends on. Whatever the sweep
        // terminated, the frontend still claimed a recording was in progress —
        // the shared timer, the live-session atom, the transport stamp, the
        // cached `recording_status`. A stale `timerModel.isRunning` does not
        // merely mislead the UI: `runRecorderSafetyStop` returns early on it
        // TWICE, so it also disables the last-resort force-stop for every
        // genuine runaway afterwards. Release the claim, and only on a result
        // that entitles it — the helper decides which do.
        //
        // `sessionId: null` because this call site targets `anyActiveUpload`: it
        // has no session of its own to release, so whichever session the
        // frontend still claims is the one released.
        //
        // A `stopInFlight` skip returns `{ attempted: false }`, which never
        // confirms a stop, so this is a no-op on that branch — the user's own stop
        // path releases each of these itself (`useMutateStopRecording.ts`,
        // `useRecordingButton.ts`, `useMutateUpdateSessionRecordingStatus.ts`).
        releaseRecordingStateAfterSweep({
          result: sweepResult,
          sessionId: null,
          jotaiStore,
          queryClient,
        });
      }

      return response;
    },

    onSuccess: ({ data }) => {
      const sessionId = data.session_id;
      // Domain event (arch §5.2 Aperture 2). Before the optimistic-update
      // try/catch so a cache-write failure can't swallow the announcement.
      emitDomainEvent(sessionCreatedEvent, { sessionId });
      onSessionCreatedExpectRoutineRuns(
        sessionId,
        (id) => {
          void queryClient.invalidateQueries({
            queryKey: WORKFLOW_KEYS.sessionRuns(id),
          });
        },
        isSessionRoutineRunsEnabled
      );
      let step = 'init';
      try {
        step = 'append_checkpoint';
        appendCheckpoint({
          key: TrackingKey.SCRIBE_NEW_SESSION,
          checkpointName: 'session_created',
        });
        const consultNote = data.consult_note;
        consultNote.brain = mapNewBrain(consultNote.brain, isFreeUser);
        const newSessionData: QuerySessionResponse = {
          ...data,
          streamingStatus: StreamingStatus.NOT_STARTED,
          chunkUploadProgress: undefined,
          backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
          userDismissedNoSoundDetectedModal: false,
          shouldShowNoteSyncChanges: false,
          shouldShowDocumentSyncChanges: false,
          isSocketError: false,
          chunksUploading: [],
          chunksUploadingFinished: false,
          abortController: new AbortController(),
          isDocumentFromNote: false,
          isAutoGeneratedNoteWarningDismissed: false,
          consult_note: consultNote,
        };
        step = 'set_session_query_data';
        queryClient.setQueryData([GET_SESSION_BY_ID, { sessionId }], newSessionData);

        const placeHolderSession: SimpleSessionResponse = {
          session_id: sessionId,
          // New list: authoritative owner from the response; old list keeps main's atom value byte-for-byte.
          kinde_gp_id: isNewSessionListEnabled
            ? data.kinde_gp_id
            : selectedReviewUser || '',
          patient: {
            name: undefined,
            gender: undefined,
            dob: undefined,
            demographic_details: undefined,
          },
          status: BaseStatus.CREATED,
          session_status: SessionStatus.EMPTY,
          is_urgent: false,
          session_group_status: SessionGroupStatus.no_action,
          duration: 0,
          is_consent: null,
        };

        step = `update_sessions_list_cache:${sessionQueryVersion ?? 'default'}`;
        switch (sessionQueryVersion) {
          case 'v3':
          case 'v2':
            queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
              {
                queryKey: [
                  sessionQueryVersion === 'v3'
                    ? GET_INFINITE_SESSIONS_WITH_PAGINATION
                    : GET_INFINITE_SESSIONS,
                ],
              },
              (oldData: InfiniteData<SessionsResponse> | undefined) => {
                if (!oldData || oldData.pages.length === 0) {
                  return oldData;
                }
                return {
                  ...oldData,
                  pages: oldData.pages.map((page, index) => {
                    if (index === 0) {
                      return {
                        ...page,
                        sessions: [placeHolderSession, ...page.sessions],
                        count: page.count + 1,
                      };
                    }
                    return page;
                  }),
                };
              }
            );
            break;
          default:
            queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldSessions) => {
              if (!oldSessions) {
                return oldSessions;
              }
              return {
                sessions: [placeHolderSession, ...oldSessions.sessions],
                count: oldSessions.sessions.length + 1,
                has_next_page: oldSessions.has_next_page,
              };
            });
        }
        appendCheckpoint({
          key: TrackingKey.SCRIBE_NEW_SESSION,
          checkpointName: 'store_updated',
        });

        step = 'dispatch_analytics';
        sessionCreated(sessionId);
        trackSessionCreated({
          session_id: sessionId,
          entry_point: entryPoint ?? SessionCreatedEntryPoint.Unknown,
          is_for_other_user: false,
          view_id: viewId,
        });

        // If active tab is upcoming, change to past when creating a new session in the past
        step = 'update_opened_tab';
        const today = new Date();
        const sessionDate = parseTimeStamp(data.created_time_local);
        if (
          openedSessionTab === openedSessionTabEnum.UPCOMING &&
          isBefore(sessionDate, today) &&
          IS_UPCOMING_SESSIONS_ENABLED()
        ) {
          setOpenedSessionTab(openedSessionTabEnum.PAST);
        }

        if (navigate) {
          // Fire-and-forget: navigation runs after `onSuccess` returns. Awaiting
          // `router.push` here would pin the mutation in `pending` until the new
          // route's data dependencies resolved, which is what trapped users on
          // the session-page skeleton when a route load stalled.
          step = 'router_push';
          // Park before push so the session page can consume the intent on mount
          // (same order as session-list Start Now: park → navigate).
          if (autoStartTranscription) {
            jotaiStore.set(pendingStartNowSessionIdAtom, {
              sessionId,
              parkedAt: Date.now(),
            });
          }
          // Shallow when the navigation stays on the sessions route: the page
          // has no data exports, so a full transition only re-runs Next.js
          // routing machinery and delays routeChangeComplete (which gates the
          // session-page loading state).
          const redirectUrl = buildSessionRedirectUrl(sessionId);
          void router.push(redirectUrl, undefined, {
            shallow:
              router.pathname === NEW_SESSION_LIST_DETAIL_ROUTE &&
              redirectUrl.startsWith(`${NEW_SESSION_LIST_PATH_PREFIX}/`),
          });
        }
      } catch (error) {
        const errorName = error instanceof Error ? error.name : typeof error;
        logger.error('[useMutateCreateNewSession] onSuccess threw', {
          errorName,
          error: sanitizeError(error),
          step,
        });
        // The step and the error class, never the raw message.
        const reported = new Error(`onSuccess failed at ${step}`);
        reported.name = errorName;
        datadogRum.addError(reported, {
          source: 'use-mutate-create-new-session.onSuccess',
          step,
        });
      }
    },

    onSettled: () => {
      // The new session is already in the list caches optimistically (onSuccess),
      // so the authoritative refetch can wait until the browser is idle instead
      // of competing with the session-page navigation and mount for network and
      // main-thread time.
      const invalidate = () => void invalidateSessionsQuery();
      if (
        typeof window !== 'undefined' &&
        typeof window.requestIdleCallback === 'function'
      ) {
        window.requestIdleCallback(invalidate, { timeout: 3000 });
      } else {
        setTimeout(invalidate, 1000);
      }
    },

    onError: (error) => {
      logger.error('[useMutateCreateNewSession] onError', {
        errorName: error.name,
        error: sanitizeError(error),
      });
      // BOTH suppressions belong to the opted-in caller: it catches this same
      // rejection and re-creates the session through offline capture, so the
      // create succeeded and a red toast would describe an operation that
      // worked. For every other caller the rejection really is a failure, and a
      // suppressed toast there is a click that does nothing and says nothing.
      // Suppress only the toast, never the log.
      if (canAttemptOfflineSessionCreate) {
        // Narrower than the shared helper on purpose: the helper does NOT
        // suppress on the startup `unknown` reading, which is permanent on
        // every flag-off client and shared with the EHR queries. A real server
        // error toasts unchanged, because both guards reject it first.
        const willFallBackToOfflineCapture =
          !hasServerResponse(error) && isNetworkCreateError(error);
        if (willFallBackToOfflineCapture) return;
        if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) return;
      }
      toast({
        tone: 'destructive',
        title: error.message,
      });
    },
  });
}

// Cache identities are single-site in `@heidi/session/contracts` (ARCFE-1001).
// These were DOUBLE-DECLARED here — the literals agreed, but two independent
// declarations of one cache key is an unguarded drift risk: a change to either
// side splits writes and reads across two caches. Imported (not just
// re-exported) because this module uses them locally too.
export { CREATE_NEW_SESSION_MUTATION_KEY };
